import { useEffect } from "react";
import TucsonHeroSection from "./components/HeroSection";
import TucsonStickyNav from "./components/StickyNav";
import TucsonProcessingTimeSection from "./components/ProcessingTimeSection";
import TucsonServicesSection from "./components/ServicesSection";
import TucsonLocalAreaSection from "./components/LocalAreaSection";
import TucsonTravelTimingSection from "./components/TravelTimingSection";
import TucsonDestinationsSection from "./components/DestinationsSection";
import TucsonTestimonialsSection from "./components/TestimonialsSection";
import TucsonAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import TucsonComparisonSection from "./components/ComparisonSection";
import TucsonMistakesSection from "./components/MistakesSection";
import TucsonHowItWorksSection from "./components/HowItWorksSection";
import TucsonFAQSection, { faqs } from "./components/FAQSection";
import TucsonHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import TucsonFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function TucsonPage() {
  useEffect(() => {
    document.title = "Emergency Passport Tucson, AZ — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/tucson/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Tucson, AZ — as fast as 24 hours. Serving University of Arizona students, Davis-Monthan AFB families, Raytheon engineers, and Southern Arizona residents. A+ BBB rated, registered State Dept. courier. No Phoenix agency drive required.");
    setMeta("geo.placename", "Tucson, Arizona");
    setMeta("geo.region", "US-AZ");
    setMeta("geo.position", "32.2226;-110.9747");
    setMeta("ICBM", "32.2226, -110.9747");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Arizona", item: "https://fastpassportcenter.com/arizona/" },
          { "@type": "ListItem", position: 3, name: "Tucson Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Tucson, AZ residents — as fast as 24 hours. Serving Southern Arizona including Marana, Oro Valley, Green Valley, and Sahuarita.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Tucson", containedInPlace: { "@type": "State", name: "Arizona" } },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-tucson";
    document.getElementById("structured-data-tucson")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data-tucson")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <TucsonHeroSection />
      <TucsonStickyNav />
      <TucsonProcessingTimeSection />
      <TucsonServicesSection />
      <TucsonLocalAreaSection />
      <TucsonTravelTimingSection />
      <TucsonDestinationsSection />
      <TucsonTestimonialsSection />
      <TucsonAcceptanceFacilitiesSection />
      <TucsonComparisonSection />
      <TucsonMistakesSection />
      <TucsonHowItWorksSection />
      <TucsonFAQSection />
      <TucsonHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/tucson" state="AZ" />
      <TucsonFinalCTA />
    </div>
  );
}